import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  IconButton,
  Alert
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Size, SizeParameterUpdateRequest, SizeParametersUpdateRequest } from '../types';
import { apiService } from '../services/api';
import LoadingButton from './LoadingButton';

interface SizeParametersDialogProps {
  open: boolean;
  onClose: () => void;
  onParametersUpdated?: () => void;
  size?: Size | null;
}

const SizeParametersDialog: React.FC<SizeParametersDialogProps> = ({
  open,
  onClose,
  onParametersUpdated,
  size
}) => {
  const { t } = useTranslation();

  const [parameters, setParameters] = useState<SizeParameterUpdateRequest[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (size && size.parameters) {
      setParameters(size.parameters.map(p => ({
        parameter_name: p.parameter_name,
        parameter_value: p.parameter_value,
        description: p.description || ''
      })));
    }
    setError(null);
  }, [size]);

  const handleParameterChange = (index: number, field: string, value: any) => {
    setParameters(prev => {
      const newParams = [...prev];
      newParams[index] = {
        ...newParams[index],
        [field]: value
      };
      return newParams;
    });
  };

  const addParameter = () => {
    setParameters(prev => [
      ...prev,
      { parameter_name: '', parameter_value: '', description: '' }
    ]);
  };

  const removeParameter = (index: number) => {
    setParameters(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    for (const param of parameters) {
      if (!param.parameter_name.trim()) {
        setError(t('sizes.validation.parameterNameRequired'));
        return false;
      }
      if (!param.parameter_value || !param.parameter_value.toString().trim()) {
        setError(t('sizes.validation.parameterValueRequired'));
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !size) return;

    try {
      setSubmitting(true);
      setError(null);

      const updateData: SizeParametersUpdateRequest = {
        parameters
      };
      
      await apiService.updateSizeParameters(size.id, updateData);
      onParametersUpdated?.();
      onClose();
    } catch (err: any) {
      console.error('Error updating parameters:', err);
      setError(err.detail || 'Error updating parameters');
    } finally {
      setSubmitting(false);
    }
  };

  const getParameterDisplayName = (paramName: string): string => {
    return t(`sizes.defaultParameters.${paramName}`) || paramName;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('sizes.editParameters')} - {size?.display_name}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('sizes.configurationHelp')}
          </Typography>
        </Alert>

        {parameters.map((param, index) => (
          <Box key={index} sx={{ 
            mb: 3, 
            p: 3, 
            border: '1px solid #e0e0e0', 
            borderRadius: 2,
            backgroundColor: '#fafafa',
            position: 'relative'
          }}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'primary.main' }}>
              {t('sizes.fields.parameterName')} #{index + 1}
            </Typography>
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} sm={3}>
                <TextField
                  label={t('sizes.fields.parameterName')}
                  value={param.parameter_name}
                  onChange={(e) => handleParameterChange(index, 'parameter_name', e.target.value)}
                  fullWidth
                  required
                  helperText={getParameterDisplayName(param.parameter_name)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  label={t('sizes.fields.parameterValue')}
                  type="text"
                  value={param.parameter_value}
                  onChange={(e) => handleParameterChange(index, 'parameter_value', e.target.value)}
                  fullWidth
                  required
                  placeholder="e.g. 1000 or 250m"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('sizes.fields.parameterDescription')}
                  value={param.description}
                  onChange={(e) => handleParameterChange(index, 'description', e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={1}>
                <IconButton
                  onClick={() => removeParameter(index)}
                  color="error"
                  disabled={parameters.length === 1}
                  sx={{ 
                    position: 'absolute',
                    top: 8,
                    right: 8
                  }}
                  title={t('common.delete')}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={addParameter}
          variant="outlined"
          color="primary"
          sx={{ mt: 2, mb: 3 }}
          size="large"
        >
          {t('sizes.addParameter')}
        </Button>

        <Box sx={{ mt: 4, p: 3, bgcolor: 'primary.light', borderRadius: 2, border: '1px solid', borderColor: 'primary.main' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.dark', fontWeight: 600 }}>
            💡 {t('sizes.commonParameterNames')}
          </Typography>
          <Typography variant="body2" component="div" sx={{ m: 0 }}>
            <Grid container spacing={1} sx={{ mt: 1 }}>
              {Object.entries({
                'questions_per_month': t('sizes.parameterDescriptions.questions_per_month'),
                'metabase_enabled': t('sizes.parameterDescriptions.metabase_enabled'),
                'presenton_enabled': t('sizes.parameterDescriptions.presenton_enabled'),
                'database_sync_enabled': t('sizes.parameterDescriptions.database_sync_enabled'),
                'max_users': t('sizes.parameterDescriptions.max_users'),
                'max_projects': t('sizes.parameterDescriptions.max_projects'),
                'max_connections_per_project': t('sizes.parameterDescriptions.max_connections_per_project'),
                'max_tables_per_connection': t('sizes.parameterDescriptions.max_tables_per_connection'),
                'max_records_per_table': t('sizes.parameterDescriptions.max_records_per_table'),
                'max_files_per_project': t('sizes.parameterDescriptions.max_files_per_project'),
                'backend_cpu_request': t('sizes.parameterDescriptions.backend_cpu_request'),
                'backend_memory_request': t('sizes.parameterDescriptions.backend_memory_request'),
                'postgres_cpu_request': t('sizes.parameterDescriptions.postgres_cpu_request'),
                'postgres_memory_request': t('sizes.parameterDescriptions.postgres_memory_request'),
                'postgres_storage_size': t('sizes.parameterDescriptions.postgres_storage_size')
              }).map(([key, description]) => (
                <Grid item xs={12} sm={6} key={key}>
                  <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, fontSize: '0.875rem' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                      {key}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={submitting}
          variant="contained"
        >
          {t('sizes.updateParameters')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default SizeParametersDialog;
